import type { ChartImage, DropdownOption, LivePlotImageOptions, LivePlotSizeOptions, PlotSpec, PrintChartAspect, RichSummary } from '../shared';
import type { ParsedValues } from './validation';

export const APP_NAME = 'iBlast';
export const APP_VERSION = '1.0.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };
export const API_PATH = '/iBlast/';

export type APIPayload = Omit<ParsedValues, 'project' | 'title'>;

export type ChartKey = 'load' | 'scaled_positive' | 'scaled_negative' | 'ce' | 'roof_scaled_rise_time' | 'roof_scaled_duration_roof' | 'q0' | 'pressure_reflection' | 'impulse_reflection';

export const PRINT_CHART_ORDER: ChartKey[] = ['load', 'scaled_positive', 'scaled_negative', 'ce', 'roof_scaled_rise_time', 'roof_scaled_duration_roof', 'q0', 'pressure_reflection', 'impulse_reflection'];

export const PRINT_CHART_ASPECT = {
  load: 'wide',
  scaled_positive: 'square',
  scaled_negative: 'square',
  ce: 'square',
  roof_scaled_rise_time: 'square',
  roof_scaled_duration_roof: 'square',
  q0: 'square',
  pressure_reflection: 'square',
  impulse_reflection: 'square'
} satisfies Record<ChartKey, PrintChartAspect>;

export const PRINT_CHART_IMAGE_OPTIONS: Record<PrintChartAspect, LivePlotImageOptions> = {
  wide: {
    width: 600,
    height: 400,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 22 }, y: 0.98, yanchor: 'top', x: 0.5, xanchor: 'center', pad: { t: 0, b: 0 } },
      margin: { l: 10, r: 10, t: 10, b: 10 },
      xaxis: { title: { font: { size: 30 } }, tickfont: { size: 22 } },
      yaxis: { title: { font: { size: 30 } }, tickfont: { size: 22 } },
      legend: { font: { size: 24 }, x: 0.4, xanchor: 'center', y: -0.2 }
    }
  },
  square: {
    width: 800,
    height: 800,
    scale: 2,
    layout: {
      font: { size: 16 },
      title: { font: { size: 22 }, y: 0.98, yanchor: 'top', x: 0.5, xanchor: 'center', pad: { t: 0, b: 0 } },
      margin: { l: 60, r: 60, t: 100, b: 100 },
      xaxis: { title: { font: { size: 30 } }, tickfont: { size: 22 } },
      yaxis: { title: { font: { size: 30 } }, tickfont: { size: 22 } },
      legend: { font: { size: 24 }, x: 0.4, xanchor: 'center', y: -0.1 }
    }
  }
};

export const LIVE_PLOT_CARD_MIN_HEIGHT = 'clamp(22rem, 72vw, 36rem)';

const SQUARE_LIVE_PLOT_SIZE = {
  fit: 'width',
  width: '100%',
  maxWidth: '39rem',
  aspectRatio: '1 / 1',
  titleFontSize: 12
} satisfies LivePlotSizeOptions;

export const LIVE_PLOT_SIZE_BY_KEY = {
  load: {
    fit: 'width',
    width: '100%',
    maxWidth: '39rem',
    aspectRatio: '5 / 3',
    titleFontSize: 16
  },
  scaled_positive: SQUARE_LIVE_PLOT_SIZE,
  scaled_negative: SQUARE_LIVE_PLOT_SIZE,
  ce: SQUARE_LIVE_PLOT_SIZE,
  roof_scaled_rise_time: SQUARE_LIVE_PLOT_SIZE,
  roof_scaled_duration_roof: SQUARE_LIVE_PLOT_SIZE,
  q0: SQUARE_LIVE_PLOT_SIZE,
  pressure_reflection: SQUARE_LIVE_PLOT_SIZE,
  impulse_reflection: SQUARE_LIVE_PLOT_SIZE
} satisfies Record<ChartKey, LivePlotSizeOptions>;

export type APIResponse = {
  summary?: RichSummary;
  plotData: Partial<Record<ChartKey, PlotSpec>>;
  warnings?: Record<string, string>;
};

// Plotly chart images are generated client-side for print/save.
export type BlastResultState = APIResponse & {
  charts?: Partial<Record<ChartKey, ChartImage>>;
};

const CHARGE_TYPE_OPTIONS: DropdownOption[] = [
  { value: '0', label: 'Hemispherical' },
  { value: '1', label: 'Spherical' }
];

const CALCULATION_TYPE_OPTIONS: DropdownOption[] = [
  { value: '1', label: 'Charge Weight, Standoff, Angle -> Pressure, Impulse' },
  { value: '2', label: 'Pressure, Impulse, angle -> Charge Weight, Standoff' },
  { value: '3', label: 'Standoff, Pressure, angle -> Charge Weight, Impulse' },
  { value: '4', label: 'Charge Weight, Pressure, angle -> Standoff, Impulse' }
];

const TARGET_TYPE_OPTIONS: DropdownOption[] = [
  { value: '0', label: 'Front Wall' },
  { value: '1', label: 'Side Wall / Roof' },
  { value: '2', label: 'Back Wall' }
];

export const DROPDOWN_OPTIONS = {
  chargeType: CHARGE_TYPE_OPTIONS,
  calculationType: CALCULATION_TYPE_OPTIONS,
  targetType: TARGET_TYPE_OPTIONS
};
